import { useEffect, useMemo, useState } from "react";
import {
  api,
  fmtMoney,
  TARIFF_LABEL,
  type Analytics as A,
  type Funnel,
  type FunnelStage,
  type Revenue,
} from "../api";
import { KpiSkeleton } from "../components/KpiCard";
import { BarTrend } from "../components/BarTrend";
import { IconTrendUp, IconKey, IconChart, IconCalendar, IconDownload } from "../components/icons";
import { bucketByWeek } from "../util/buckets";
import { downloadCsv } from "../util/csv";

const PERIODS = [
  { days: 7, label: "7 kun" },
  { days: 30, label: "30 kun" },
  { days: 90, label: "90 kun" },
];

export function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<A | null>(null);
  const [revenue, setRevenue] = useState<Revenue | null>(null);
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // MRR breakdown davrga bog'liq emas — bir marta yuklanadi.
  useEffect(() => {
    api.revenue().then(setRevenue).catch((e) => setErr((e as Error).message));
  }, []);

  useEffect(() => {
    setData(null); setFunnel(null); setErr(null);
    api.analytics(days).then(setData).catch((e) => setErr((e as Error).message));
    api.funnel(days).then(setFunnel).catch((e) => setErr((e as Error).message));
  }, [days]);

  const weekly = useMemo(
    () => (data ? bucketByWeek(data.new_workshops_by_day) : []),
    [data],
  );

  // Sinov soni — tariff_distribution dan (mavjud ma'lumot), revenue bo'lmasa ham ishlaydi.
  const trialCount = useMemo(() => {
    if (!data) return 0;
    return data.tariff_distribution.find((t) => t.tariff === "trial")?.count ?? 0;
  }, [data]);

  const topWorks = useMemo(() => {
    if (!data || data.top_workshops.length === 0) return 1;
    return Math.max(1, ...data.top_workshops.map((w) => w.works_count));
  }, [data]);

  // QR havola ulanish darajasi — mavjud QR voronkasidan hosil qilinadi.
  // Ko'rsatilgan = birinchi bosqich, Ulangan = oxirgi bosqich. shown=0 bo'lsa "—".
  const linkRate = useMemo(() => {
    if (!funnel || funnel.qr.length === 0) return null;
    const shown = funnel.qr[0]?.count ?? 0;
    if (shown <= 0) return null;
    const linked = funnel.qr[funnel.qr.length - 1]?.count ?? 0;
    return Math.round((linked / shown) * 100);
  }, [funnel]);

  function exportCsv() {
    if (!data) return;
    const header = ["Sana", "Yangi ustaxonalar"];
    const rows = data.new_workshops_by_day.map((p) => [p.date, p.count]);
    downloadCsv(`analytics-${days}-${new Date().toISOString().slice(0, 10)}.csv`, [header, ...rows]);
  }

  return (
    <div className="analytics-page">
      <div className="page-head">
        <div>
          <h1>Analitika</h1>
          <p className="page-sub">Platforma bo'yicha o'sish va faollik</p>
        </div>
        <div className="actions">
          <div className="chips">
            {PERIODS.map((p) => (
              <button
                key={p.days}
                className={days === p.days ? "active" : ""}
                onClick={() => setDays(p.days)}
              >{p.label}</button>
            ))}
          </div>
          <button className="btn" onClick={exportCsv} disabled={!data}>
            <IconDownload /> CSV eksport
          </button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {data ? (
        <div className="kpis cols-4">
          <div className="kpi primary">
            <div className="kpi-head">
              <div className="label">{`Yangi ustaxonalar (${days} kun)`}</div>
              <span className="kpi-ico"><IconTrendUp /></span>
            </div>
            <div className="value">{data.new_workshops_total}</div>
          </div>
          <div className="kpi">
            <div className="kpi-head">
              <div className="label">Faollashtirilgan kodlar</div>
              <span className="kpi-ico"><IconKey /></span>
            </div>
            <div className="value">{data.codes_activated_total}</div>
          </div>
          <div className="kpi">
            <div className="kpi-head">
              <div className="label">Jami ishlar</div>
              <span className="kpi-ico"><IconChart /></span>
            </div>
            <div className="value">{data.works_total}</div>
          </div>
          <div className="kpi">
            <div className="kpi-head">
              <div className="label">Davr (kun)</div>
              <span className="kpi-ico"><IconCalendar /></span>
            </div>
            <div className="value">{data.days}</div>
          </div>
        </div>
      ) : (
        <div className="kpis cols-4">
          {Array.from({ length: 4 }).map((_, i) => <KpiSkeleton key={i} />)}
        </div>
      )}

      <div className="analytics-grid">
        <div className="panel">
          <div className="panel-title">
            <h2>Haftalik yangi ustaxonalar</h2>
            <span className="muted">{days} kun</span>
          </div>
          {data ? (
            <BarTrend buckets={weekly} emptyText="Bu davrda yangi ustaxona yo'q" />
          ) : (
            <div className="skel" style={{ height: 160 }} />
          )}
        </div>

        <div className="panel">
          <div className="panel-title">
            <h2>Tariflar bo'yicha</h2>
          </div>
          {revenue ? (
            <table className="table an-plans">
              <thead>
                <tr>
                  <th>Tarif</th>
                  <th className="num">Soni</th>
                  <th className="num">Narx</th>
                  <th className="num">Jami</th>
                  <th className="num">Ulush</th>
                </tr>
              </thead>
              <tbody>
                {revenue.tariff_breakdown.map((b) => (
                  <tr key={b.tariff}>
                    <td>
                      <span className={`an-plan-badge ${b.tariff}`}>{TARIFF_LABEL[b.tariff]}</span>
                    </td>
                    <td className="num">{b.count}</td>
                    <td className="num">{fmtMoney(b.price)}</td>
                    <td className="num">{fmtMoney(b.subtotal)}</td>
                    <td className="num">
                      <div className="an-share">
                        <div className="an-share-track">
                          <div className="an-share-fill" style={{ width: `${Math.round((b.share || 0) * 100)}%` }} />
                        </div>
                        <span>{Math.round((b.share || 0) * 100)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Sinov tarifi BEPUL — narxi yo'q, MRR ga 0 qo'shadi (ulush 0%). */}
                <tr>
                  <td><span className="an-plan-badge trial">{TARIFF_LABEL.trial}</span></td>
                  <td className="num">{trialCount}</td>
                  <td className="num">Bepul</td>
                  <td className="num">—</td>
                  <td className="num">
                    <div className="an-share">
                      <div className="an-share-track">
                        <div className="an-share-fill" style={{ width: "0%" }} />
                      </div>
                      <span>0%</span>
                    </div>
                  </td>
                </tr>
                <tr className="an-mrr-row">
                  <td colSpan={3}><strong>Jami MRR</strong></td>
                  <td className="num an-mrr-total">{fmtMoney(revenue.mrr)}</td>
                  <td className="num"><strong>100%</strong></td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="empty">Ma'lumot yo'q</div>
          )}
          {revenue && revenue.active_trials > 0 && (
            <div className="note an-trial-note">
              Sinov quvuri: <strong>{revenue.active_trials}</strong> faol sinov.
              Standart tarifga o'tsa: <strong>{fmtMoney(revenue.trial_floor_estimate)}</strong>{" "}
              <span className="muted">(taxmin — MRR emas)</span>
            </div>
          )}
        </div>
      </div>

      {/* Konversiya voronkasi */}
      <div className="panel">
        <div className="panel-title">
          <h2>Konversiya voronkasi</h2>
          <span className="muted">Lead → To'lovchi ({days} kun oynasi)</span>
        </div>
        {funnel ? (
          <FunnelView stages={funnel.activation} />
        ) : (
          <div className="skel" style={{ height: 180 }} />
        )}
        <div className="muted an-foot">
          Eslatma: lead va ustaxonalar qat'iy bog'lanmagan — bu bosqich-sanoqli voronka.
        </div>
      </div>

      {/* QR / havola voronkasi — PMF signali, vizual urg'u berilgan */}
      <div className="panel an-qr">
        <div className="panel-title">
          <h2>QR / havola voronkasi</h2>
          <span className="muted">PMF signali ({days} kun)</span>
        </div>
        {funnel ? (
          <div className="an-qr-grid">
            <FunnelView stages={funnel.qr} />
            <div className="an-qr-rate">
              <div className="lbl">Havola ulanish darajasi</div>
              <div className="val">{linkRate === null ? "—" : `${linkRate}%`}</div>
            </div>
          </div>
        ) : (
          <div className="skel" style={{ height: 120 }} />
        )}
      </div>

      <div className="panel">
        <div className="panel-title">
          <h2>Top 10 ustaxona (ishlar soni)</h2>
        </div>
        {!data ? (
          <div className="empty">Yuklanmoqda…</div>
        ) : data.top_workshops.length === 0 ? (
          <div className="empty">Ma'lumot yo'q</div>
        ) : (
          <table className="table an-top">
            <thead>
              <tr>
                <th style={{ width: 48 }}>#</th>
                <th>Ustaxona</th>
                <th>Ishlar soni</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.top_workshops.map((w, i) => (
                <tr key={w.id}>
                  <td><span className="an-rank">{i + 1}</span></td>
                  <td><strong className="an-top-name" title={w.name}>{w.name}</strong></td>
                  <td>
                    <div className="an-top-bar">
                      <span className="an-top-num">{w.works_count}</span>
                      <div className="an-top-track">
                        <div className="an-top-fill" style={{ width: `${Math.round((w.works_count / topWorks) * 100)}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="right">
                    <a className="icon-btn" href={`#/workshops/${w.id}`}>Ochish →</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Bitta umumiy voronka komponenti — Haydovchilar sahifasidagi bilan bir xil
// `.funnel` / `.funnel-stage` tuzilmasidan foydalanadi (yagona vizual til).
function FunnelView({ stages }: { stages: FunnelStage[] }) {
  const top = Math.max(1, ...stages.map((s) => s.count));
  return (
    <div className="funnel">
      {stages.map((s) => {
        const width = Math.max(2, (s.count / top) * 100);
        return (
          <div className="funnel-stage" key={s.key}>
            <div className="bar-wrap">
              <div className="bar-fill" style={{ width: `${width}%` }} />
              <div className="bar-label">{s.label}</div>
            </div>
            <div className="stage-meta">
              <strong>{s.count}</strong>
              {s.pct_of_prev !== null && (
                <span className="stage-pct"> · {Math.round(s.pct_of_prev * 100)}%</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
